import React, { useMemo, useState } from 'react';
import {
  HEADER_COLOR, GREY, money, variancePercent, SortableTh, EyeToggleIcon, useTableSort,
} from './weeklyTableHelpers';

// Occupancy arrives as a 2dp float; display it as a whole-number percent.
const percent = (value) => `${Math.round(value ?? 0)}%`;

// The rows under Total Trips in the expanded view, in render order.
//
// Four of them count RIDES: Total Trips = Billable Trips + Cancel Earlies + No Charges, and
// Call Outs are the late cancellations inside Billable Trips, so they are shown but are not a
// term in that sum. (Total Trips itself is rendered above this list, because it is the only
// row carrying a variance column.)
//
// The two Bookings rows count PASSENGERS. A ride carrying four employees is one ride and four
// bookings, so these always read at least as high as the ride counts, and they are named
// Bookings so the two units cannot be read as the same thing. They sit directly beneath their
// ride equivalents — Total Bookings under Total Trips, Billable Bookings under Billable Trips
// — so each pair reads together rather than being separated by the write-off rows. The ride
// sum above therefore skips over them, which is why it is spelled out.
//
// Both are scoped to the same rides and date range as the counts around them, and appear only
// for a per-seat company — see WeeklyOverviewReport#booking_counts_for.
const TRIP_BREAKDOWN_ROWS = [
  { label: 'Billable Trips', key: 'billable_trips', testId: 'billable' },
  { label: 'Total Bookings', key: 'total_bookings', testId: 'total-bookings', perSeatOnly: true },
  { label: 'Billable Bookings', key: 'billable_bookings', testId: 'billable-bookings', perSeatOnly: true },
  { label: 'Cancel Earlies', key: 'cancelled_early_trips', testId: 'cancelled-early' },
  { label: 'Call Outs', key: 'call_out_trips', testId: 'call-outs' },
  { label: 'No Charges', key: 'no_charge_trips', testId: 'no-charges' },
];

/**
 * The breakdown rows for one company.
 *
 * The two booking rows are shown only for a company on a per-seat billing model, because a
 * company billing per vehicle is not billed per passenger and the count would be noise on its
 * row. That is decided by the SERVER, which omits the keys for a split model — branching on
 * their presence keeps the rule in one place rather than restating "which models are per seat"
 * here as well.
 *
 * @param {Object} current - The company's current-period figures.
 * @returns {Array<Object>} the rows to render.
 */
const breakdownRowsFor = (current) => (
  TRIP_BREAKDOWN_ROWS.filter((row) => !row.perSeatOnly || current?.[row.key] != null)
);

const VarianceArrow = ({ value }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 19 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={value === 0 || value === null || value === undefined ? 'hidden' : ''}
  >
    {value > 0 ? (
      <path
        d="M1.2729 10.9217L0.306803 9.98025C-0.102268 9.58163 -0.102268 8.93706 0.306803 8.54268L8.76237 0.298962C9.17144 -0.0996541 9.83291 -0.0996541 10.2376 0.298962L18.6932 8.53844C19.1023 8.93706 19.1023 9.58163 18.6932 9.97601L17.7271 10.9174C17.3137 11.3203 16.6391 11.3118 16.2344 10.9005L11.2429 5.79478V17.9823C11.2429 18.5463 10.7773 19 10.1985 19H8.80589C8.2271 19 7.76145 18.5463 7.76145 17.9823V5.79478L2.76557 10.9047C2.36086 11.3203 1.68633 11.3288 1.2729 10.9217Z"
        fill="#00FF00"
      />
    ) : (
      <path
        d="M1.2729 8.07834L0.306803 9.01975C-0.102268 9.41837 -0.102268 10.0629 0.306803 10.4573L8.76237 18.701C9.17144 19.0997 9.83291 19.0997 10.2376 18.701L18.6932 10.4616C19.1023 10.0629 19.1023 9.41837 18.6932 9.02399L17.7271 8.08258C17.3137 7.67972 16.6391 7.6882 16.2344 8.09954L11.2429 13.2052V1.01774C11.2429 0.453745 10.7773 0 10.1985 0H8.80589C8.2271 0 7.76145 0.453745 7.76145 1.01774V13.2052L2.76557 8.0953C2.36086 7.67972 1.68633 7.67124 1.2729 8.07834Z"
        fill="#FF0000"
      />
    )}
  </svg>
);

/**
 * Weekly Overview — Executive view table (flag-on). Ten columns with a
 * client-derived Profit and payload-driven cost/occupancy figures. Cost per head
 * is reported as a proposed/actual pair, mirroring the occupancy pair. The eye
 * action expands the same week-over-week comparison sub-row the legacy
 * WeeklyTable used.
 *
 * @param {Object} props
 * @param {Array<Object>} props.data - Per-company rows from weekly_summary.
 *   The comparison period is no longer passed in: the payload decides it. The
 *   backend emits `previous: null` and nil variances for periods that have no
 *   comparison (Year to Date, Custom), so the sub-row gates on the payload's
 *   presence rather than on a magic period value.
 * @returns {JSX.Element}
 */
const ExecutiveTable = ({ data }) => {
  const [expandedRows, setExpandedRows] = useState([]);

  const handleRowClick = (id) => {
    setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  };

  // Profit is derived here so it is both rendered and sortable through the same
  // getByPath path used by every other column.
  //
  // It is null unless BOTH sides are known. Treating a dashed figure as zero would subtract
  // a real payout from nothing and print a confident loss next to the very cell admitting we
  // could not price the earnings.
  const withDerived = useMemo(
    () =>
      data.map((item) => {
        const earnings = item.current?.total_corporate_earnings;
        const payout = item.current?.payout;
        const known = earnings !== null && earnings !== undefined && payout !== null && payout !== undefined;

        return { ...item, profit: known ? earnings - payout : null };
      }),
    [data]
  );

  const { sortConfig, handleSort, sortedRows: sortedData } = useTableSort(withDerived);

  // Presence gate, not a value gate. The backend already suppressed the
  // comparison for periods that do not have one, so an absent `previous` block
  // means "not applicable" and the whole comparison column is hidden.

  return (
    <div className="w-full h-full" style={{ fontFamily: 'Ubuntu, system-ui, sans-serif', fontWeight: 300 }}>
      <div className="overflow-auto w-full scrollbar" style={{ maxHeight: '600px' }}>
        <table className="border-collapse w-full" data-testid="weekly-exec-table">
          <thead>
            <tr>
              <SortableTh label="Company" sortKey="company_name" sortConfig={sortConfig} onSort={handleSort} testId="exec-th-company_name" className="left-0 z-20" />
              <SortableTh label="Driver Pay" sortKey="current.payout" sortConfig={sortConfig} onSort={handleSort} testId="exec-th-current.payout" />
              <SortableTh label="Client Pay" sortKey="current.total_corporate_earnings" sortConfig={sortConfig} onSort={handleSort} testId="exec-th-current.total_corporate_earnings" />
              <SortableTh label="Profit" sortKey="profit" sortConfig={sortConfig} onSort={handleSort} testId="exec-th-profit" />
              <SortableTh label="Proposed Cost per head" sortKey="proposed_cost_per_head" sortConfig={sortConfig} onSort={handleSort} testId="exec-th-proposed_cost_per_head" />
              <SortableTh label="Actual Cost per head" sortKey="actual_cost_per_head" sortConfig={sortConfig} onSort={handleSort} testId="exec-th-actual_cost_per_head" />
              <SortableTh label="Billable Trips" sortKey="current.billable_trips" sortConfig={sortConfig} onSort={handleSort} testId="exec-th-current.billable_trips" />
              <SortableTh label="Proposed Occupancy" sortKey="proposed_occupancy" sortConfig={sortConfig} onSort={handleSort} testId="exec-th-proposed_occupancy" />
              <SortableTh label="Occupancy" sortKey="occupancy" sortConfig={sortConfig} onSort={handleSort} testId="exec-th-occupancy" />
              <th className="border-b p-2 text-left sticky top-0 z-10 bg-white" style={{ color: HEADER_COLOR }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item) => {
              const showVariance = item.previous != null;
              return (
              <React.Fragment key={item.company_id}>
                <tr className="border-t border-b p-2">
                  <td
                    data-testid={`exec-cell-company-${item.company_id}`}
                    title={item.company_name}
                    className="border-t border-b p-2 text-gray-700 font-medium text-left sticky left-0 z-10 bg-white truncate"
                    style={{ maxWidth: '220px' }}
                  >{item.company_name}</td>
                  <td className="border-t text-gray-700 border-b p-2 text-left">{money(item.current?.payout)}</td>
                  <td className="border-t text-gray-700 border-b p-2 text-left">{money(item.current?.total_corporate_earnings)}</td>
                  <td className="border-t text-gray-700 border-b p-2 text-left">{money(item.profit)}</td>
                  <td className="border-t text-gray-700 border-b p-2 text-left" data-testid={`exec-cell-proposed_cost_per_head-${item.company_id}`}>{money(item.proposed_cost_per_head)}</td>
                  <td className="border-t text-gray-700 border-b p-2 text-left" data-testid={`exec-cell-actual_cost_per_head-${item.company_id}`}>{money(item.actual_cost_per_head)}</td>
                  <td className="border-t text-gray-700 border-b p-2 text-left" data-testid={`exec-cell-billable-trips-${item.company_id}`}>{item.current?.billable_trips}</td>
                  <td className="border-t text-gray-700 border-b p-2 text-left">{percent(item.proposed_occupancy)}</td>
                  <td className="border-t text-gray-700 border-b p-2 text-left">{percent(item.occupancy)}</td>
                  <td className="border-t border-b p-2 space-x-1">
                    <button
                      type="button"
                      data-testid={`exec-row-expand-${item.company_id}`}
                      onClick={() => handleRowClick(item.company_id)}
                      className="inline-flex items-center justify-center"
                      style={{
                        background: GREY, width: '41px', height: '41px', borderRadius: '5px',
                      }}
                    >
                      <EyeToggleIcon expanded={expandedRows.includes(item.company_id)} />
                    </button>
                  </td>
                </tr>
                {expandedRows.includes(item.company_id) && (
                  <tr data-testid={`exec-row-detail-${item.company_id}`}>
                    <td colSpan="10" className="p-0 pr-2">
                      <table className="table-fixed w-full border-collapse">
                        <thead className="w-full">
                          <tr>
                            <th className="border-b p-2 text-right"></th>
                            <th className="text-gray-700 border-b p-2 font-semibold text-right" data-testid={`exec-detail-current-header-${item.company_id}`}>{item.current?.date_range}</th>
                            {showVariance && (
                              <th className="text-gray-700 border-b p-2 font-semibold text-right" data-testid={`exec-detail-previous-header-${item.company_id}`}>{item.previous?.date_range}</th>
                            )}
                            {showVariance && <th className="border-b p-2 text-right"></th>}
                          </tr>
                        </thead>
                        <tbody className="w-full pr-2">
                          <tr>
                            <td className="text-gray-700 border-t border-b p-2 text-right">Driver Pay</td>
                            <td className="text-gray-700 border-t border-b p-2 text-right">{money(item.current?.payout)}</td>
                            {showVariance && (
                              <td className="text-gray-700 border-t border-b p-2 text-right">{money(item.previous?.payout)}</td>
                            )}
                            {showVariance && (
                              <td className="text-gray-700 border-t border-b p-2 text-right">
                                <div className="flex p-2 items-center justify-end gap-1">
                                  {variancePercent(item.payout_variance)}
                                  <VarianceArrow value={item.payout_variance} />
                                </div>
                              </td>
                            )}
                          </tr>
                          <tr>
                            <td className="text-gray-700 border-t border-b p-2 text-right">Total Client Pay</td>
                            <td className="text-gray-700 border-t border-b p-2 text-right">{money(item.current?.total_corporate_earnings)}</td>
                            {showVariance && (
                              <td className="text-gray-700 border-t border-b p-2 text-right">{money(item.previous?.total_corporate_earnings)}</td>
                            )}
                            {showVariance && (
                              <td className="text-gray-700 border-t border-b p-2 text-right">
                                <div className="flex p-2 items-center justify-end gap-1">
                                  {variancePercent(item.corporate_earnings_variance)}
                                  <VarianceArrow value={item.corporate_earnings_variance} />
                                </div>
                              </td>
                            )}
                          </tr>
                          <tr>
                            <td className="text-gray-700 border-t border-b p-2 text-right">Total Trips</td>
                            <td className="text-gray-700 border-t border-b p-2 text-right" data-testid={`exec-breakdown-total-${item.company_id}`}>{item.current?.trips}</td>
                            {showVariance && (
                              <td className="text-gray-700 border-t border-b p-2 text-right">{item.previous?.trips}</td>
                            )}
                            {showVariance && (
                              <td className="text-gray-700 border-t border-b p-2 text-right">
                                <div className="flex p-2 items-center justify-end gap-1">
                                  {variancePercent(item.trip_variance)}
                                  <VarianceArrow value={item.trip_variance} />
                                </div>
                              </td>
                            )}
                          </tr>
                          {breakdownRowsFor(item.current).map(({ label, key, testId }) => (
                            <tr key={key}>
                              <td className="text-gray-700 border-t border-b p-2 text-right">{label}</td>
                              <td className="text-gray-700 border-t border-b p-2 text-right" data-testid={`exec-breakdown-${testId}-${item.company_id}`}>{item.current?.[key]}</td>
                              {showVariance && (
                                <td className="text-gray-700 border-t border-b p-2 text-right" data-testid={`exec-breakdown-${testId}-prev-${item.company_id}`}>{item.previous?.[key]}</td>
                              )}
                              {/* No variance figure: the payload carries none for these rows, and
                                  computing one here would duplicate the report's nil-guarding. */}
                              {showVariance && <td className="text-gray-700 border-t border-b p-2 text-right" />}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExecutiveTable;
